import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import DatePicker from 'react-datepicker';
import { FaCar } from 'react-icons/fa';
import { getCarsBySite, updateDriverTran } from 'redux/booking/car/carBookingSlice';
import UploadDocument from 'components/common/upload/UploadDocument';
import { Modal, Button } from 'components/common';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const labelClass = 'tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5';
const errClass = 'tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0';

const TransactionDetailDriverTransFormModal = ({ showModal, setshowModal, transaction, driverTran }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue } = useForm();
  const [images, setImages] = useState([]);
  const fileTypes = 'image/*';
  const fileNote = 'Only image/* files type are allowed. The file size is maximum of 20Mb';
  const dispatch = useDispatch();

  const params = {
    from_date: transaction.from_date,
    to_date: transaction.to_date,
    site_id: transaction.site_id,
  };

  useEffect(() => {
    // Preload cars by site (kept for parity with legacy behavior).
    dispatch(getCarsBySite(params)).catch((error) => console.error(error));
    // preload once on open; `params` is rebuilt every render, so depending on it would refetch on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateConvert = (date) => new Date(date);

  useEffect(() => {
    if (driverTran) {
      setValue('departure_begin_time', dateConvert(driverTran.departure_begin_time || transaction.from_date));
      setValue('departure_end_time', dateConvert(driverTran.departure_end_time || transaction.to_date));
      setValue('departure_begin_kilometer', driverTran.departure_begin_kilometer);
      setValue('departure_end_kilometer', driverTran.departure_end_kilometer);
      setValue('notes', driverTran.notes);
      setValue('parking_fee', driverTran.parking_fee);
    } else {
      setValue('departure_begin_time', dateConvert(transaction.from_date));
      setValue('departure_end_time', dateConvert(transaction.to_date));
    }
    // seed only when driverTran changes; re-running on transaction refresh would overwrite the user's edited departure times
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, driverTran]);

  const closeModal = () => setshowModal(false);

  const onSubmit = (data) => {
    try {
      if (driverTran) {
        dispatch(updateDriverTran({ transactionId: transaction.id, driverTranId: driverTran.id, data: { ...data, images } }));
        closeModal();
      } else {
        toast.error('Error update driver.');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      title="Edit transaction"
      size="lg"
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="driver-tran-form" variant="primary">Update</Button>
        </>
      )}
    >
      <form id="driver-tran-form" onSubmit={handleSubmit(onSubmit)}>
        <h6 className="tw-text-sm tw-mb-4 tw-text-slate-700">
          Driver name:{' '}
          <span className="tw-text-blue-600 tw-font-medium tw-inline-flex tw-items-center tw-gap-1">
            {driverTran?.driver?.name} / <FaCar /> {driverTran?.car?.name} ({driverTran?.car?.model})
          </span>
        </h6>

        <div className="tw-mb-4">
          <label className={labelClass}>Departure time</label>
          <div className="tw-grid tw-grid-cols-2 tw-gap-2">
            <Controller
              control={control}
              name="departure_begin_time"
              rules={{ required: 'Departure begin time must be required' }}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  className={inputClass}
                  placeholderText="From"
                  timeIntervals={5}
                  dateFormat="dd-MM-yyyy h:mm aa"
                  showTimeSelect
                  showTimeSelectOnly
                />
              )}
            />
            <Controller
              control={control}
              name="departure_end_time"
              rules={{ required: 'Departure end time must be required' }}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  className={inputClass}
                  placeholderText="To"
                  timeIntervals={5}
                  dateFormat="dd-MM-yyyy h:mm aa"
                  showTimeSelect
                  showTimeSelectOnly
                />
              )}
            />
          </div>
        </div>

        <div className="tw-mb-4">
          <label className={labelClass}>Departure (km)</label>
          <div className="tw-grid tw-grid-cols-2 tw-gap-2">
            <div>
              <input type="number" min={0} placeholder="From" className={inputClass}
                {...register('departure_begin_kilometer', { required: 'Departure from (km) must be required' })} />
              {errors.departure_begin_kilometer && <p className={errClass}>{errors.departure_begin_kilometer.message}</p>}
            </div>
            <div>
              <input type="number" min={0} placeholder="To" className={inputClass}
                {...register('departure_end_kilometer', { required: 'Departure to (km) must be required' })} />
              {errors.departure_end_kilometer && <p className={errClass}>{errors.departure_end_kilometer.message}</p>}
            </div>
          </div>
        </div>

        <div className="tw-mb-4">
          <label className={labelClass}>Parking fee (VND)</label>
          <input type="number" placeholder="Parking fee" className={inputClass} {...register('parking_fee')} />
        </div>

        <div className="tw-mb-4">
          <label className={labelClass}>Notes</label>
          <textarea rows={6} placeholder="Notes" className={inputClass} {...register('notes')} />
        </div>

        <div>
          <label className={labelClass}>Attachments</label>
          <UploadDocument files={images} setFiles={setImages} fileTypes={fileTypes} note={fileNote} />
        </div>
      </form>
    </Modal>
  );
};

TransactionDetailDriverTransFormModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  transaction: PropTypes.object.isRequired,
  driverTran: PropTypes.object,
};

export default TransactionDetailDriverTransFormModal;
